import React, {createContext, useContext, useEffect, useMemo, useState} from "react";
import {Locale} from "./LocaleProvider";
import useDebouncedMemo from "@sevenoutman/use-debounced-memo";
import {StringParam, useQueryParam, withDefault} from "hooks/useQueryParam";
import {useLocation, useNavigate} from "react-router-dom";
import {useRole} from "./Role";


type Option = {
    path: string,
    label: React.ReactChild,
}

type QueryBarType = {
    q: string;
    setQ: (value: string | undefined, replace?: boolean) => any;
    option?: Option;
    setOption: React.Dispatch<React.SetStateAction<Option | undefined>>
    optionActive: boolean;
}


export const queryOptions: Option[] = [
    {
        label: <><Locale>users.plural</Locale> (<Locale>common.phone</Locale>)</>,
        path: "/users?q_by=phone",
    },
    {
        label: <><Locale>users.plural</Locale> (<Locale>users.name</Locale>)</>,
        path: "/users?q_by=name",
    },
    {
        label: <><Locale>users.plural</Locale> (<Locale>users.vendor_name</Locale>)</>,
        path: "/users?q_by=vendor_name",
    },
    {
        label: <Locale>service_providers.plural</Locale>,
        path: "/service_providers",
    },
]

const context = createContext<QueryBarType>({
    q: '',
    setQ: (v) => v,
    setOption: o => o,
    option: queryOptions[0],
    optionActive: false,
})


export const QueryBarProvider: React.FC = ({children}) => {

    const location = useLocation();
    const navigate = useNavigate();
    const [q, _setQ] = useQueryParam('q', withDefault(StringParam, ''))
    const [option, setOption] = useState<Option | undefined>(() => {
        return queryOptions[0]
    })


    const optionActive = useMemo(() => new RegExp(`${option?.path?.split('?')[0] || ''}$`).test(location.pathname), [location.pathname, option?.path])

    const setQ: typeof _setQ = (...args) => {
        const [path, params] = option?.path.split('?') || []
        const optionsQueryParams = (new URLSearchParams(params || ''))
        const locationQueryParams = (new URLSearchParams(location.search || ''))
        let matchingQueryParams = true;
        for (const [key, value] of optionsQueryParams.entries()) {
            if (!(locationQueryParams.has(key) && locationQueryParams.get(key) === value)) {
                matchingQueryParams = false;
                break;
            }
        }

        if ((!optionActive) || (optionActive && !matchingQueryParams)) {
            navigate(`${path}?${params || ''}&q=${args[0] || ''}`, {replace: args[1] || !matchingQueryParams});
        } else {
            _setQ(...args);
        }
    }

    return <context.Provider value={{
        q,
        setQ,
        option,
        setOption,
        optionActive,
    }}>
        {children}
    </context.Provider>
}

export const useQueryBar = (option?: Option | (typeof queryOptions)[number]["path"]) => {
    const qb = useContext(context);
    useEffect(() => {
        if (!option) {
            return;
        }
        if (typeof option === "string") {
            const o = queryOptions.find(o => o.path === option);
            if (o) {
                qb.setOption(o)
            }
        } else {
            qb.setOption(option)
        }
    }, [option])

    const debouncedQ = useDebouncedMemo(() => qb.q, [qb.q], 500);

    return {
        ...qb,

        debouncedQ,
    }
}
